import Dropdown, {Anchor} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import Popup from '@jetbrains/ring-ui/components/popup/popup'
import {differenceInSeconds, isBefore, min, parseISO} from 'date-fns'
import plur from 'plur'
import {memo, useMemo} from 'react'

import {usePipelineRunArg} from '../../../../PipelineRunPage/PipelineRunPage.hooks'
import JobIconStatus from '../../../JobIconStatus/JobIconStatus'

import styles from './OptimizedTIme.css'

import SvgIcon from 'src/components/common/SvgIcon/SvgIcon'
import useMyId from 'src/hooks/useMyId'
import {getBuildStatusIconFromBuild} from 'src/selectors'
import {restApi} from 'src/services/rest'
import {formatDuration} from 'src/utils/dateTimeUtils'
import {emptyArray} from 'src/utils/empty'

function OptimizedTime() {
  const myId = useMyId()
  const arg = usePipelineRunArg()

  const {jobs, queuedDate} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const run = data?.entities?.builds?.[data?.result!]
      return {
        run,
        queuedDate: run?.queuedDate,
        jobs: run?.['snapshot-dependencies']?.build ?? emptyArray,
      }
    },
  })

  const reusedJobs = useMemo(() => {
    if (queuedDate == null) {
      return []
    }
    const headQueued = parseISO(queuedDate)
    return jobs
      .filter(job => job.startDate != null && isBefore(parseISO(job.startDate), headQueued))
      .map(job => {
        const to = job.finishDate != null ? min([parseISO(job.finishDate), headQueued]) : headQueued
        return {
          job,
          saved: differenceInSeconds(to, parseISO(job.startDate!)),
        }
      })
  }, [queuedDate, jobs])
  const savedTotal = reusedJobs.reduce((acc, {saved}) => acc + saved, 0)

  if (savedTotal === 0) {
    return null
  }

  return (
    <div>
      <div className={styles.label}>{'Optimized time'}</div>
      <Dropdown
        anchor={
          <Anchor className={styles.optimized}>
            <SvgIcon className={styles.icon} icon={'hourglass'} />
            {formatDuration(savedTotal)}
          </Anchor>
        }
      >
        <Popup className={styles.reusedPopup}>
          <H3>{`${reusedJobs.length} ${plur('Job', reusedJobs.length)} Reused`}</H3>
          {reusedJobs.map(({job, saved}) => (
            <div className={styles.reusedItem} key={job.id}>
              <JobIconStatus
                parallelized={job?.parallelized}
                icon={getBuildStatusIconFromBuild(job, myId)}
                status={job.status}
                className={styles.icon}
              />
              {job.buildType?.name}
              <div className={styles.reusedItemInfo}>
                {`Run ${job.number}, saved ${formatDuration(saved)}`}
              </div>
            </div>
          ))}
        </Popup>
      </Dropdown>
    </div>
  )
}

export default memo(OptimizedTime)
